/**
 * useCrypto Session Management
 *
 * X3DH + Double Ratchet session creation and restoration.
 * Extracted from useCrypto.ts for maintainability.
 *
 * Functions accept `get`/`set` from the Zustand store to avoid circular imports
 * (same pattern as useCrypto-group.ts and useCrypto-recovery.ts).
 */

import {
  createSession,
  restoreSession,
  toBase64,
  fromBase64,
  type CryptoSession,
  type SerializedRatchetState,
  type PreKeyBundle,
} from "@kollegeplus/crypto";
import { api } from "../lib/api";
import {
  saveSession,
  loadSession,
  loadSessionsForPeer,
  deleteSession,
  type StoredSession,
} from "../lib/crypto-storage";
import { e2eeLog } from "../lib/e2ee-logger";
import type { ServerDeviceKeys } from "@kollegeplus/shared";
import type { CryptoState } from "./useCrypto";

/** F15d: Sender-side ratchet liveness window.
 *
 *  Send-path entry points load the StoredSession and check `peerLastSeen`.
 *  If older than this TTL → delete the session and force a fresh X3DH.
 *  Undefined peerLastSeen means "no signal yet" (e.g. backward-compat with
 *  pre-F15d records) — we keep the session in that case.
 *
 *  7 days matches phantom-device deactivation in cleanup.service.ts and
 *  the pending_session_reset_events TTL: a peer that's been silent for a
 *  week is almost certainly gone or rotated and re-keyed.
 */
const RATCHET_LIVENESS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Return true if `stored` is so stale we should drop it and force fresh X3DH. */
function isRatchetStale(stored: StoredSession): boolean {
  if (!stored.peerLastSeen) return false; // no signal — backward-compat, keep
  const lastSeen = Date.parse(stored.peerLastSeen);
  if (Number.isNaN(lastSeen)) return false; // corrupt timestamp — keep
  return Date.now() - lastSeen > RATCHET_LIVENESS_TTL_MS;
}

// ─── getOrCreateSession ───────────────────────────────────────────────────────

/**
 * Get or create a session for a peer (first available device — legacy DM path).
 */
export async function getOrCreateSession(
  peerId: string,
  get: () => CryptoState,
  set: (fn: (s: CryptoState) => Partial<CryptoState>) => void,
): Promise<{ session: CryptoSession; peerDeviceId: string }> {
  const state = get();
  if (!state.deviceKeys || !state.deviceId) {
    throw new Error("E2EE not initialized");
  }

  // Check if we have an existing session in memory
  for (const [key, session] of state.sessions) {
    if (typeof key !== "string") continue;
    if (key.startsWith(`${peerId}:`)) {
      const peerDeviceId = key.split(":").slice(1).join(":");
      return { session, peerDeviceId };
    }
  }

  // Try to restore from IndexedDB
  const storedSessions = await loadSessionsForPeer(peerId);
  if (storedSessions.length > 0) {
    const stored = storedSessions[0]!;

    // F15d: ratchet liveness — if the peer hasn't successfully decrypted to
    // us in over a week, treat the ratchet as dead and force fresh X3DH.
    // Skips sessions with no peerLastSeen (backward compat — never evict on
    // first send post-update).
    if (isRatchetStale(stored)) {
      e2eeLog.info(
        "F15d: evicting stale ratchet to", peerId, stored.peerDeviceId,
        "(lastSeen:", stored.peerLastSeen, ")",
      );
      await deleteSession(peerId, stored.peerDeviceId);
      // Fall through to X3DH below.
    } else {
      const ratchetState = JSON.parse(stored.ratchetState) as SerializedRatchetState;
      const ad = fromBase64(stored.associatedData);
      const remoteIdentityKey = fromBase64(stored.remoteIdentityKey);

      const session = restoreSession(ratchetState, ad, remoteIdentityKey);
      const sessionKey = `${peerId}:${stored.peerDeviceId}`;

      set((s) => {
        const sessions = new Map(s.sessions);
        sessions.set(sessionKey, session);
        return { sessions };
      });

      return { session, peerDeviceId: stored.peerDeviceId };
    }
  }

  // No existing session — fetch peer's keys and create new session via X3DH
  const peerKeysResponse = await api.post<{
    keys: Record<string, Array<ServerDeviceKeys & { remainingPreKeys?: number }>>;
  }>("/users/keys", { userIds: [peerId] });

  const peerDevices = peerKeysResponse.keys[peerId];
  if (!peerDevices || peerDevices.length === 0) {
    throw new Error(`No device keys found for user ${peerId}`);
  }

  // Use first available device
  const peerDevice = peerDevices[0]!;

  // If the server tells us our own pre-keys are low, replenish (async, don't block)
  get().checkAndReplenishPreKeys().catch(() => {});

  // Build PreKeyBundle from server response
  const bundle: PreKeyBundle = {
    identityKey: fromBase64(peerDevice.identityKey),
    signingKey: fromBase64(peerDevice.signingKey),
    signedPreKey: {
      keyId: peerDevice.signedPreKey.keyId,
      publicKey: fromBase64(peerDevice.signedPreKey.publicKey),
      signature: fromBase64(peerDevice.signedPreKey.signature),
    },
    preKey: peerDevice.preKey
      ? {
          keyId: peerDevice.preKey.keyId,
          publicKey: fromBase64(peerDevice.preKey.publicKey),
        }
      : null,
  };

  // Perform X3DH to establish session
  const session = createSession(state.deviceKeys.identityKeyPair, bundle);
  const sessionKey = `${peerId}:${peerDevice.deviceId}`;

  // Persist session state to IndexedDB
  const serializedState = session.exportState();
  const ad = new Uint8Array([
    ...state.deviceKeys.identityKeyPair.curve25519.publicKey,
    ...bundle.identityKey,
  ]);

  await saveSession({
    peerId,
    peerDeviceId: peerDevice.deviceId,
    ratchetState: JSON.stringify(serializedState),
    associatedData: toBase64(ad),
    remoteIdentityKey: toBase64(bundle.identityKey),
    updatedAt: new Date().toISOString(),
  });

  set((s) => {
    const sessions = new Map(s.sessions);
    sessions.set(sessionKey, session);
    return { sessions };
  });

  return { session, peerDeviceId: peerDevice.deviceId };
}

// ─── getOrCreateSessionForDevice ─────────────────────────────────────────────

/**
 * Get or create a session for a specific peer device (multi-device path).
 */
export async function getOrCreateSessionForDevice(
  peerId: string,
  peerDeviceId: string,
  peerDevice: ServerDeviceKeys,
  get: () => CryptoState,
  set: (fn: (s: CryptoState) => Partial<CryptoState>) => void,
): Promise<CryptoSession> {
  const state = get();
  if (!state.deviceKeys || !state.deviceId) {
    throw new Error("E2EE not initialized");
  }

  const sessionKey = `${peerId}:${peerDeviceId}`;

  // Check in-memory cache
  const existing = state.sessions.get(sessionKey);
  if (existing) return existing;

  // Try to restore from IndexedDB
  const stored = await loadSession(peerId, peerDeviceId);
  if (stored) {
    // F15d: ratchet liveness — drop sessions whose peerLastSeen is older
    // than RATCHET_LIVENESS_TTL_MS. Phantom devices never decrypt to us
    // so peerLastSeen stays at session-creation time and they fall out
    // here, forcing the next send to do a fresh X3DH (or no-op if the
    // device is gone server-side).
    if (isRatchetStale(stored)) {
      e2eeLog.info(
        "F15d: evicting stale ratchet to", peerId, peerDeviceId,
        "(lastSeen:", stored.peerLastSeen, ")",
      );
      await deleteSession(peerId, peerDeviceId);
      // Fall through to X3DH below.
    } else if (stored.remoteIdentityKey === peerDevice.identityKey) {
      // Validate that the stored identity key still matches the server's current key.
      // If the peer's device was reset (new keys), the identity key will differ —
      // in that case discard the stale session and build a fresh X3DH one below.
      const ratchetState = JSON.parse(stored.ratchetState) as SerializedRatchetState;
      const ad = fromBase64(stored.associatedData);
      const remoteIdentityKey = fromBase64(stored.remoteIdentityKey);
      const session = restoreSession(ratchetState, ad, remoteIdentityKey);

      set((s) => {
        const sessions = new Map(s.sessions);
        sessions.set(sessionKey, session);
        return { sessions };
      });

      return session;
    } else {
      // Identity key mismatch — device was reset, delete stale session
      e2eeLog.warn(`Identity key mismatch for ${peerId}:${peerDeviceId} — rebuilding session`);
      await deleteSession(peerId, peerDeviceId);
    }
  }

  // No session — create new via X3DH
  const bundle: PreKeyBundle = {
    identityKey: fromBase64(peerDevice.identityKey),
    signingKey: fromBase64(peerDevice.signingKey),
    signedPreKey: {
      keyId: peerDevice.signedPreKey.keyId,
      publicKey: fromBase64(peerDevice.signedPreKey.publicKey),
      signature: fromBase64(peerDevice.signedPreKey.signature),
    },
    preKey: peerDevice.preKey
      ? {
          keyId: peerDevice.preKey.keyId,
          publicKey: fromBase64(peerDevice.preKey.publicKey),
        }
      : null,
  };

  const session = createSession(state.deviceKeys.identityKeyPair, bundle);

  // Persist session
  const serializedState = session.exportState();
  const ad = new Uint8Array([
    ...state.deviceKeys.identityKeyPair.curve25519.publicKey,
    ...bundle.identityKey,
  ]);

  await saveSession({
    peerId,
    peerDeviceId,
    ratchetState: JSON.stringify(serializedState),
    associatedData: toBase64(ad),
    remoteIdentityKey: toBase64(bundle.identityKey),
    updatedAt: new Date().toISOString(),
  });

  set((s) => {
    const sessions = new Map(s.sessions);
    sessions.set(sessionKey, session);
    return { sessions };
  });

  return session;
}
